<script setup lang="ts">
import { computed } from 'vue'
import imgCalArrow from '@/assets/right arrow@2x.png'

const month = defineModel<number>('month', { required: true })
const year = defineModel<number>('year', { required: true })

const props = withDefaults(
  defineProps<{
    /** `YYYY-MM-DD` — highlights the selected day */
    selectedYmd?: string
    /** 新建订单等窄表单：相对默认尺寸 80% 等比缩小 */
    compact?: boolean
  }>(),
  { selectedYmd: '', compact: false },
)

const emit = defineEmits<{
  pick: [ymd: string]
}>()

const monthLabel = computed(() =>
  new Date(year.value, month.value, 1).toLocaleString('en-US', { month: 'short' }),
)

type GridCell = { kind: 'lead' | 'day' | 'trail'; day: number; y: number; mon: number }

const grid = computed((): GridCell[] => {
  const y = year.value
  const m = month.value
  const firstDow = new Date(y, m, 1).getDay()
  const dim = new Date(y, m + 1, 0).getDate()
  const prevDim = new Date(y, m, 0).getDate()
  const cells: GridCell[] = []

  for (let i = 0; i < firstDow; i++) {
    const d = prevDim - firstDow + 1 + i
    let mon = m - 1
    let yy = y
    if (mon < 0) {
      mon = 11
      yy -= 1
    }
    cells.push({ kind: 'lead', day: d, y: yy, mon })
  }
  for (let d = 1; d <= dim; d++) {
    cells.push({ kind: 'day', day: d, y, mon: m })
  }
  let t = 1
  while (cells.length % 7 !== 0 || cells.length < 35) {
    let mon = m + 1
    let yy = y
    if (mon > 11) {
      mon = 0
      yy += 1
    }
    cells.push({ kind: 'trail', day: t++, y: yy, mon })
  }
  return cells
})

function pad(n: number) {
  return String(n).padStart(2, '0')
}

function ymdForCell(c: GridCell) {
  return `${c.y}-${pad(c.mon + 1)}-${pad(c.day)}`
}

function isSelected(c: GridCell) {
  return props.selectedYmd && ymdForCell(c) === props.selectedYmd
}

function pickCell(c: GridCell) {
  month.value = c.mon
  year.value = c.y
  emit('pick', ymdForCell(c))
}

function prevMonth() {
  if (month.value === 0) {
    month.value = 11
    year.value -= 1
  } else {
    month.value -= 1
  }
}

function nextMonth() {
  if (month.value === 11) {
    month.value = 0
    year.value += 1
  } else {
    month.value += 1
  }
}

function prevYear() {
  year.value -= 1
}

function nextYear() {
  year.value += 1
}
</script>

<template>
  <div class="cal-mini" :class="{ 'cal-mini--compact': compact }">
    <div class="cal-head">
      <div class="cal-head-month">
        <button type="button" class="cal-nav" aria-label="Previous month" @click="prevMonth">
          <img :src="imgCalArrow" class="cal-nav-ic cal-nav-ic--prev" alt="" />
        </button>
        <span class="cal-head-month-label">{{ monthLabel }}</span>
        <button type="button" class="cal-nav" aria-label="Next month" @click="nextMonth">
          <img :src="imgCalArrow" class="cal-nav-ic cal-nav-ic--next" alt="" />
        </button>
      </div>
      <div class="cal-head-year">
        <button type="button" class="cal-nav" aria-label="Previous year" @click="prevYear">
          <img :src="imgCalArrow" class="cal-nav-ic cal-nav-ic--prev" alt="" />
        </button>
        <span class="cal-head-year-label">{{ year }}</span>
        <button type="button" class="cal-nav" aria-label="Next year" @click="nextYear">
          <img :src="imgCalArrow" class="cal-nav-ic cal-nav-ic--next" alt="" />
        </button>
      </div>
    </div>
    <div class="cal-weeks">
      <span>Su</span><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span>
    </div>
    <div class="cal-grid">
      <button
        v-for="(c, i) in grid"
        :key="i"
        type="button"
        :class="['cal-cell', c.kind, { 'cal-sel': isSelected(c) }]"
        @click="pickCell(c)"
      >
        {{ c.day }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.cal-mini {
  --cal-text-font: 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  --cal-cell-size: 40px;
  --cal-cell-gap: 8px;
  box-sizing: border-box;
  width: fit-content;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  padding: 24px;
  font-family: var(--cal-text-font);
}

/* 顶部：‹ Oct ›    ‹ 2021 › */
.cal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 48px;
  margin-bottom: 24px;
}

.cal-head-month,
.cal-head-year {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  flex: 1;
  min-width: 0;
}

/* 月份：SF Pro Medium 14/14 #000 右对齐 */
.cal-head-month-label {
  flex: 1;
  min-width: 36px;
  font-family: var(--cal-text-font);
  font-size: 14px;
  font-weight: 500;
  font-style: normal;
  line-height: 14px;
  letter-spacing: 0;
  color: #000000;
  text-align: right;
  white-space: nowrap;
}

/* 年份：SF Pro Medium 14/14 #000 右对齐 */
.cal-head-year-label {
  flex: 1;
  min-width: 44px;
  font-family: var(--cal-text-font);
  font-size: 14px;
  font-weight: 500;
  font-style: normal;
  line-height: 14px;
  letter-spacing: 0;
  color: #000000;
  text-align: right;
  white-space: nowrap;
}

.cal-nav {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
}

.cal-nav-ic {
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  display: block;
  object-fit: contain;
}

.cal-nav-ic--prev {
  transform: scaleX(-1);
}

.cal-nav:hover {
  opacity: 0.65;
}

.cal-weeks {
  display: grid;
  grid-template-columns: repeat(7, var(--cal-cell-size));
  gap: var(--cal-cell-gap);
  margin-bottom: 16px;
}

/* 星期：SF Pro Regular 14/14 #000 右对齐 */
.cal-weeks span {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-sizing: border-box;
  width: var(--cal-cell-size);
  height: 14px;
  padding: 0;
  font-family: var(--cal-text-font);
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 14px;
  letter-spacing: 0;
  color: #000000;
  text-align: right;
}

.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, var(--cal-cell-size));
  gap: var(--cal-cell-gap);
}

/* 日期：40×40、间距 8px、数字居中 */
.cal-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: var(--cal-cell-size);
  height: var(--cal-cell-size);
  padding: 0;
  margin: 0;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 6px;
  font-family: var(--cal-text-font);
  font-size: 14px;
  font-weight: 500;
  font-style: normal;
  line-height: 14px;
  letter-spacing: 0;
  color: #000000;
  text-align: center;
  cursor: pointer;
}

/* 上月/下月补位日期 */
.cal-cell.lead,
.cal-cell.trail {
  color: #999999;
}

.cal-cell.lead:hover:not(.cal-sel),
.cal-cell.trail:hover:not(.cal-sel) {
  border-color: #cccccc;
  color: #999999;
}

/* 悬停外框：40×40 圆角 6 1px #000（当月）；补位日为浅灰边框 */
.cal-cell.day:hover:not(.cal-sel) {
  border: 1px solid #000000;
  background: transparent;
}

/* 已选日期：40×40 黑底圆角 6，数字 #FFF */
.cal-cell.cal-sel,
.cal-cell.cal-sel.lead,
.cal-cell.cal-sel.trail {
  width: var(--cal-cell-size);
  height: var(--cal-cell-size);
  border: none;
  background: #000000;
  border-radius: 6px;
  color: #ffffff;
  -webkit-text-fill-color: #ffffff;
}

/* 等比 80%（仅新建订单等场景，相对默认 40px 格） */
.cal-mini--compact {
  --cal-cell-size: 32px;
  --cal-cell-gap: 6px;
  padding: 19px;
  border-radius: 13px;
  box-shadow: 0 6px 13px rgba(0, 0, 0, 0.1);
}

.cal-mini--compact .cal-head {
  gap: 38px;
  margin-bottom: 19px;
}

.cal-mini--compact .cal-head-month,
.cal-mini--compact .cal-head-year {
  gap: 19px;
}

.cal-mini--compact .cal-head-month-label,
.cal-mini--compact .cal-head-year-label {
  min-width: 29px;
  font-size: 11px;
  line-height: 11px;
}

.cal-mini--compact .cal-nav {
  width: 19px;
  height: 19px;
}

.cal-mini--compact .cal-nav-ic {
  width: 13px;
  height: 13px;
  min-width: 13px;
  min-height: 13px;
}

.cal-mini--compact .cal-weeks {
  margin-bottom: 13px;
}

.cal-mini--compact .cal-weeks span {
  height: 11px;
  font-size: 11px;
  line-height: 11px;
}

.cal-mini--compact .cal-cell {
  font-size: 11px;
  line-height: 11px;
  border-radius: 5px;
}

.cal-mini--compact .cal-cell.cal-sel,
.cal-mini--compact .cal-cell.cal-sel.lead,
.cal-mini--compact .cal-cell.cal-sel.trail {
  border-radius: 5px;
  color: #ffffff;
  -webkit-text-fill-color: #ffffff;
}

</style>
